const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const Cast = require('../../util/cast');
const formatMessage = require('format-message');

const blockIconURI = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMzQiIGhlaWdodD0iMzQiIHJ4PSI4IiBmaWxsPSIjMEE5Q0Q4Ii8+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMTEiIGZpbGw9IndoaXRlIi8+PGNpcmNsZSBjeD0iMTYiIGN5PSIxOCIgcj0iMiIgZmlsbD0iIzBBOUNEOCIvPjxjaXJjbGUgY3g9IjI0IiBjeT0iMTgiIHI9IjIiIGZpbGw9IiMwQTlDRDgiLz48cGF0aCBkPSJNMTYgMjQuNUMxNy4xNjExIDI1LjUwNDkgMTguNTk4MyAyNiAyMCAyNkMyMS40MDE3IDI2IDIyLjgzODkgMjUuNTA0OSAyNCAyNC41IiBzdHJva2U9IiMwQTlDRDgiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+PC9zdmc+';
const menuIconURI = blockIconURI;

const VIDEO_STATE = {
    OFF: 'off',
    ON: 'on',
    ON_FLIPPED: 'on-flipped'
};

const EXPRESSION_MENU = [
    ['smil', 'smile'],
    ['mund åben', 'mouthOpen'],
    ['øjne lukket', 'eyeClosure'],
    ['øjenbryn op', 'browRaise'],
    ['fløjten', 'lipPucker'],
    ['øjne vidt åbne', 'eyeWiden'],
    ['rynker bryn', 'browFurrow'],
    ['rynker næse', 'noseWrinkle']
];

const EMOTION_MENU = [
    ['glad', 'joy'],
    ['ked af det', 'sadness'],
    ['afsky', 'disgust'],
    ['vred', 'anger'],
    ['bange', 'fear']
];

const FACE_POINT_MENU = [
    ['næsetip', '12'],
    ['venstre øje', '17'],
    ['højre øje', '18'],
    ['mund venstre', '20'],
    ['mund højre', '24'],
    ['hage', '2']
];

const loadScriptOnce = (url, globalName) => {
    const globalScope = typeof window !== 'undefined' ? window : global;
    if (globalScope[globalName]) return Promise.resolve(globalScope[globalName]);

    globalScope.__datakoderenScriptPromises = globalScope.__datakoderenScriptPromises || {};
    if (globalScope.__datakoderenScriptPromises[url]) return globalScope.__datakoderenScriptPromises[url];

    globalScope.__datakoderenScriptPromises[url] = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => resolve(globalScope[globalName]);
        script.onerror = () => reject(new Error(`Kunne ikke hente scriptet ${url}`));
        document.head.appendChild(script);
    });

    return globalScope.__datakoderenScriptPromises[url];
};

class Scratch3FaceSensingBlocks {
    constructor (runtime) {
        this.runtime = runtime;
        this._loopHandle = null;
        this._detectorPromise = null;
        this._latestFace = null;
        this._isRunningInference = false;
        this._dimensions = [480, 360];
        this._videoState = VIDEO_STATE.ON;
        this._videoTransparency = 50;

        this._startLoop();
    }

    getInfo () {
        return {
            id: 'faceSensing',
            name: formatMessage({
                id: 'faceSensing.categoryName',
                default: 'Ansigt',
                description: 'Navn på ansigtssensing-udvidelsen'
            }),
            blockIconURI,
            menuIconURI,
            color1: '#0A9CD8',
            color2: '#087FB0',
            color3: '#06658D',
            blocks: [
                {
                    opcode: 'goToFacePoint',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'faceSensing.goToFacePoint',
                        default: 'gå til [FACE_POINT]',
                        description: 'Flyt sprite til et punkt på ansigtet'
                    }),
                    arguments: {
                        FACE_POINT: {
                            type: ArgumentType.STRING,
                            menu: 'FACE_POINT',
                            defaultValue: '12'
                        }
                    }
                },
                {
                    opcode: 'whenExpressionDetected',
                    blockType: BlockType.HAT,
                    isEdgeActivated: true,
                    shouldRestartExistingThreads: false,
                    text: formatMessage({
                        id: 'faceSensing.whenExpressionDetected',
                        default: 'når [EXPRESSION] ses',
                        description: 'Hat-blok for ansigtsudtryk'
                    }),
                    arguments: {
                        EXPRESSION: {
                            type: ArgumentType.STRING,
                            menu: 'EXPRESSION',
                            defaultValue: 'smile'
                        }
                    }
                },
                {
                    opcode: 'isExpressionDetected',
                    blockType: BlockType.BOOLEAN,
                    text: formatMessage({
                        id: 'faceSensing.isExpressionDetected',
                        default: 'ses [EXPRESSION]?',
                        description: 'Om et ansigtsudtryk ses'
                    }),
                    arguments: {
                        EXPRESSION: {
                            type: ArgumentType.STRING,
                            menu: 'EXPRESSION',
                            defaultValue: 'smile'
                        }
                    }
                },
                {
                    opcode: 'expressionAmount',
                    blockType: BlockType.REPORTER,
                    text: formatMessage({
                        id: 'faceSensing.expressionAmount',
                        default: 'mængde af [EXPRESSION]',
                        description: 'Mængden af et ansigtsudtryk'
                    }),
                    arguments: {
                        EXPRESSION: {
                            type: ArgumentType.STRING,
                            menu: 'EXPRESSION',
                            defaultValue: 'smile'
                        }
                    }
                },
                {
                    opcode: 'whenEmotionDetected',
                    blockType: BlockType.HAT,
                    isEdgeActivated: true,
                    shouldRestartExistingThreads: false,
                    text: formatMessage({
                        id: 'faceSensing.whenEmotionDetected',
                        default: 'når følelsen [EMOTION] ses',
                        description: 'Hat-blok for følelser'
                    }),
                    arguments: {
                        EMOTION: {
                            type: ArgumentType.STRING,
                            menu: 'EMOTION',
                            defaultValue: 'joy'
                        }
                    }
                },
                {
                    opcode: 'isTopEmotion',
                    blockType: BlockType.BOOLEAN,
                    text: formatMessage({
                        id: 'faceSensing.isTopEmotion',
                        default: 'er følelsen [EMOTION]?',
                        description: 'Om den stærkeste følelse matcher'
                    }),
                    arguments: {
                        EMOTION: {
                            type: ArgumentType.STRING,
                            menu: 'EMOTION',
                            defaultValue: 'joy'
                        }
                    }
                },
                {
                    opcode: 'emotionAmount',
                    blockType: BlockType.REPORTER,
                    text: formatMessage({
                        id: 'faceSensing.emotionAmount',
                        default: 'styrke af følelsen [EMOTION]',
                        description: 'Styrken af en følelse'
                    }),
                    arguments: {
                        EMOTION: {
                            type: ArgumentType.STRING,
                            menu: 'EMOTION',
                            defaultValue: 'joy'
                        }
                    }
                },
                {
                    opcode: 'setVideoState',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'faceSensing.setVideoState',
                        default: 'sæt kamera [VIDEO_STATE]',
                        description: 'Tænd eller sluk kameraet'
                    }),
                    arguments: {
                        VIDEO_STATE: {
                            type: ArgumentType.STRING,
                            menu: 'VIDEO_STATE',
                            defaultValue: VIDEO_STATE.ON
                        }
                    }
                },
                {
                    opcode: 'setVideoTransparency',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'faceSensing.setVideoTransparency',
                        default: 'sæt kameragennemsigtighed til [TRANSPARENCY]',
                        description: 'Sæt kameraets gennemsigtighed'
                    }),
                    arguments: {
                        TRANSPARENCY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 50
                        }
                    }
                }
            ],
            menus: {
                FACE_POINT: FACE_POINT_MENU.map(([text, value]) => ({text, value})),
                EXPRESSION: EXPRESSION_MENU.map(([text, value]) => ({text, value})),
                EMOTION: EMOTION_MENU.map(([text, value]) => ({text, value})),
                VIDEO_STATE: [
                    {text: 'tændt', value: VIDEO_STATE.ON},
                    {text: 'slukket', value: VIDEO_STATE.OFF},
                    {text: 'tændt spejlet', value: VIDEO_STATE.ON_FLIPPED}
                ]
            }
        };
    }

    _startLoop () {
        if (this._loopHandle) return;
        this._loopHandle = setInterval(() => {
            this._stepLoop().catch(() => {});
        }, 150);
    }

    _getVideoDevice () {
        return this.runtime.ioDevices && this.runtime.ioDevices.video;
    }

    _applyVideoState () {
        const video = this._getVideoDevice();
        if (!video) return;

        if (this._videoState === VIDEO_STATE.OFF) {
            video.disableVideo();
            return;
        }

        video.enableVideo();
        video.mirror = this._videoState !== VIDEO_STATE.ON_FLIPPED;
        video.setPreviewGhost(this._videoTransparency);
    }

    async _ensureDetector (imageElement) {
        if (this._detectorPromise) return this._detectorPromise;

        this._detectorPromise = loadScriptOnce(
            'https://download.affectiva.com/js/3.2.1/affdex.js',
            'affdex'
        ).then(affdex => new Promise((resolve, reject) => {
            const detector = new affdex.PhotoDetector(
                imageElement,
                this._dimensions[0],
                this._dimensions[1],
                affdex.FaceDetectorMode.LARGE_FACES
            );
            detector.detectAllEmotions();
            detector.detectAllExpressions();
            detector.addEventListener('onInitializeSuccess', () => resolve(detector));
            detector.addEventListener('onInitializeFailure', error => reject(new Error(String(error))));
            detector.start();
        }));

        return this._detectorPromise;
    }

    async _stepLoop () {
        if (this._isRunningInference) return;
        const video = this._getVideoDevice();
        if (!video || this._videoState === VIDEO_STATE.OFF) return;

        const frame = video.getFrame({
            format: 'image-data',
            dimensions: this._dimensions
        });
        if (!frame) return;

        this._isRunningInference = true;
        try {
            const detector = await this._ensureDetector(frame);
            this._latestFace = await new Promise(resolve => {
                let resolved = false;
                const timeout = setTimeout(() => {
                    if (!resolved) {
                        resolved = true;
                        detector.removeEventListener('onImageResultsSuccess', onSuccess);
                        resolve(null);
                    }
                }, 1000);

                const onSuccess = faces => {
                    if (resolved) return;
                    resolved = true;
                    clearTimeout(timeout);
                    detector.removeEventListener('onImageResultsSuccess', onSuccess);
                    resolve(Array.isArray(faces) && faces.length ? faces[0] : null);
                };

                detector.addEventListener('onImageResultsSuccess', onSuccess);
                detector.process(frame, 0);
            });
        } finally {
            this._isRunningInference = false;
        }
    }

    _toScratchCoordinates (point) {
        return {
            x: point.x - (this._dimensions[0] / 2),
            y: (this._dimensions[1] / 2) - point.y
        };
    }

    _expressionValue (expression) {
        if (!this._latestFace || !this._latestFace.expressions) return 0;
        return Number(this._latestFace.expressions[expression] || 0);
    }

    _emotionValue (emotion) {
        if (!this._latestFace || !this._latestFace.emotions) return 0;
        return Number(this._latestFace.emotions[emotion] || 0);
    }

    _topEmotion () {
        if (!this._latestFace || !this._latestFace.emotions) return '';
        let topEmotion = '';
        let topValue = -1;
        Object.keys(this._latestFace.emotions).forEach(emotion => {
            const value = Number(this._latestFace.emotions[emotion] || 0);
            if (value > topValue) {
                topValue = value;
                topEmotion = emotion;
            }
        });
        return topEmotion;
    }

    goToFacePoint (args, util) {
        if (!this._latestFace || !this._latestFace.featurePoints || !util.target || !util.target.setXY) return;
        const pointIndex = Cast.toNumber(args.FACE_POINT);
        const point = this._latestFace.featurePoints[pointIndex];
        if (!point) return;
        const {x, y} = this._toScratchCoordinates(point);
        util.target.setXY(x, y, false);
    }

    whenExpressionDetected (args) {
        return this.isExpressionDetected(args);
    }

    isExpressionDetected (args) {
        return this._expressionValue(Cast.toString(args.EXPRESSION)) > 0.5;
    }

    expressionAmount (args) {
        return Math.round(this._expressionValue(Cast.toString(args.EXPRESSION)) * 100);
    }

    whenEmotionDetected (args) {
        return this.isTopEmotion(args);
    }

    isTopEmotion (args) {
        return this._topEmotion() === Cast.toString(args.EMOTION);
    }

    emotionAmount (args) {
        return Math.round(this._emotionValue(Cast.toString(args.EMOTION)) * 100);
    }

    setVideoState (args) {
        const state = Cast.toString(args.VIDEO_STATE);
        this._videoState = Object.values(VIDEO_STATE).includes(state) ? state : VIDEO_STATE.ON;
        this._applyVideoState();
    }

    setVideoTransparency (args) {
        this._videoTransparency = Math.max(0, Math.min(100, Cast.toNumber(args.TRANSPARENCY)));
        this._applyVideoState();
    }
}

module.exports = Scratch3FaceSensingBlocks;
